/**
 * Uploads into a folder (docs/FOLDERS.md §2, §8): files dropped on (or picked in) a folder's page go into it; a dropped
 * folder's files go into folders of its names, made where missing (`create_folders`), merged into those there. A zip
 * asks whether it's kept as a file or unpacked into folders (the answer remembered in this browser). A file meeting an
 * item of its title there asks Keep both, Replace or Skip, once for the whole drop when said for all; one whose
 * content is somewhere already says where, with Open, Keep both or Skip.
 */
import { signal } from "@preact/signals";
import { api, send } from "../../api/client.ts";
import type {
  Crumb,
  FolderOut,
  ItemDetail,
  Job,
  ObjEntity,
  SameTitle,
  SpaceOut,
  Unpacked,
} from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { type DropTarget, droppedPath } from "../../lib/fileDrop.ts";
import { isZipItem, pathText } from "../../lib/folders.ts";
import { followJob } from "../../lib/jobs.ts";
import { spaceApi, spaceUrl } from "../../lib/paths.ts";
import { bump } from "../../lib/resource.ts";
import { store, stored } from "../../lib/storage.ts";
import { fold } from "../../lib/text.ts";
import { toast } from "../../lib/toast.ts";

/** What this viewer said to do with a dropped zip, kept: `keep` it as a file, or `unpack` it. */
const ZIP_KEY = "revisa.folders.zip";
const CREATED = 201;
/** How many "Already here" a drop says one by one (the rest in one line). */
const SAID = 3;

type ZipChoice = "keep" | "unpack";

/** A question an upload waits on, and how it's answered. */
export type Question = { id: number } & (
  | { kind: "zip"; name: string; answer: (a: { choice: ZipChoice; remember: boolean }) => void }
  | { kind: "title"; name: string; folder: string; answer: (a: { choice: SameTitle; all: boolean }) => void }
);
/** The last question's number. */
let asking = 0;

/** The question on screen (one at a time: an upload asks, then goes on). */
export const asked = signal<Question | null>(null);

/** Asks `q` (on screen, `asked`) and waits for its answer, which closes it. */
function ask<A>(q: (answer: (a: A) => void) => DistributiveOmit<Question>): Promise<A> {
  asking += 1;
  const id = asking;
  return new Promise((done) => {
    let once = false;
    asked.value = {
      id,
      ...q((a) => {
        if (!once) {
          once = true;
          asked.value = null;
          done(a);
        }
      }),
    } as Question;
  });
}

/** A question without its number (given it by `ask`). */
type DistributiveOmit<Q> = Q extends unknown ? Omit<Q, "id"> : never;

const isZip = (f: File) => isZipItem({ file: { mime: f.type, name: f.name } });

/** Where in `base` (none: the top level, out of the triage area) a file goes: its folder there, by the path it came in. */
function placeOf(base: string | null, f: File): { folder?: string; create_folders?: boolean } {
  const dirs = (droppedPath(f) ?? "").split("/").slice(0, -1).filter(Boolean);
  if (dirs.length === 0) {
    return { folder: base ?? "/" };
  }
  return { folder: [base ?? "", ...dirs].join("/"), create_folders: true };
}

/** Sends one file: the item, and whether it's new (not the one there of its title, kept by `skip`). */
async function sendFile(space: SpaceOut, f: File, query: Record<string, string | boolean | null | undefined>) {
  const modified = f.lastModified > 0 ? new Date(f.lastModified).toISOString() : null;
  const res = await send("POST", spaceApi(space, "/files"), {
    query: { name: f.name, modified, ...query },
    raw: { data: f, type: f.type },
  });
  return { item: (await res.json()) as ItemDetail, made: res.status === CREATED };
}

/** What a drop did, to say at the end. */
interface Tally {
  added: number;
  skipped: number;
  unpacked: number;
  /** New items whose content was somewhere already: where. */
  twins: { made: ItemDetail; there: { id: string; title: string; folder_path: Crumb[] } }[];
}

/** Unpacks the zip item `zip` where it is, then (unpacked) lets it go: it was only a way to bring its files. */
async function unpack(space: SpaceOut, zip: ItemDetail, tally: Tally): Promise<void> {
  const r = await api.post<Unpacked | Job>(spaceApi(space, `/items/${zip.id}/unpack`), {});
  if ("action" in r) {
    await followJob(space, r);
  } else {
    tally.unpacked += r.added;
  }
  await api.del(spaceApi(space, `/items/${zip.id}`));
}

/** Whether a dropped zip is unpacked: as remembered, or asked. */
async function zipChoice(name: string): Promise<ZipChoice> {
  const kept = stored(ZIP_KEY);
  if (kept === "keep" || kept === "unpack") {
    return kept;
  }
  const a = await ask<{ choice: ZipChoice; remember: boolean }>((answer) => ({ kind: "zip", name, answer }));
  if (a.remember) {
    store(ZIP_KEY, a.choice);
  }
  return a.choice;
}

/** Says how a drop went: what came in, and what was somewhere already (with Open, Keep both, Skip). */
function sayTally(space: SpaceOut, tally: Tally, name: string) {
  if (tally.added > 0) {
    toast(t("folders.uploaded", { count: tally.added, name }));
  }
  if (tally.unpacked > 0) {
    toast(t("folders.unpacked", { count: tally.unpacked }));
  }
  if (tally.skipped > 0) {
    toast(t("folders.skipped", { count: tally.skipped }));
  }
  for (const { made, there } of tally.twins.slice(0, SAID)) {
    const where = pathText([...there.folder_path, { name: there.title }]);
    toast(t("folders.alreadyHere", { path: where }), "info", undefined, [
      { label: t("folders.openIt"), href: spaceUrl(space, `/items/${there.id}`) },
      { label: t("folders.keepBoth"), onClick: () => undefined },
      {
        label: t("folders.skipIt"),
        onClick: () => {
          api.del(spaceApi(space, `/items/${made.id}`)).then(bump, (e: unknown) => toast(String(e), "error"));
        },
      },
    ]);
  }
  if (tally.twins.length > SAID) {
    toast(t("folders.alreadyHereMore", { count: tally.twins.length - SAID }));
  }
}

/** Uploads `files` into `folder` (none: the root) as the module says, each one of `entity`'s if there's one. */
async function uploadInto(
  space: SpaceOut,
  folder: Pick<FolderOut, "id" | "name"> | null,
  files: File[],
  entity?: Pick<ObjEntity, "id">,
) {
  const tally: Tally = { added: 0, skipped: 0, unpacked: 0, twins: [] };
  const name = folder?.name ?? t("folders.root");
  // Said for all: what a file meeting one of its title does from then on.
  let forAll: SameTitle | undefined;
  for (const f of files) {
    const place = { ...placeOf(folder?.id ?? null, f), entity: entity?.id };
    // A loose zip: kept, or unpacked (asked once, unless remembered).
    // biome-ignore lint/performance/noAwaitInLoops: one upload at a time, each perhaps asking first.
    const unpacking = !droppedPath(f) && isZip(f) && (await zipChoice(f.name)) === "unpack";
    // Not said yet: `skip` finds out whether one of its title is there (it's answered with that one).
    let r = await sendFile(space, f, { ...place, same_title: forAll ?? "skip" });
    if (!(r.made || forAll) && fold(r.item.title ?? "") === fold(f.name)) {
      const a = await ask<{ choice: SameTitle; all: boolean }>((answer) => ({
        kind: "title",
        name: f.name,
        folder: name,
        answer,
      }));
      if (a.all) {
        forAll = a.choice;
      }
      if (a.choice === "skip") {
        tally.skipped += 1;
        continue;
      }
      r = await sendFile(space, f, { ...place, same_title: a.choice });
    }
    if (unpacking) {
      await unpack(space, r.item, tally);
      continue;
    }
    tally.added += 1;
    const there = r.made ? r.item.already_here?.[0] : undefined;
    if (there) {
      tally.twins.push({ made: r.item, there });
    }
  }
  bump();
  sayTally(space, tally, name);
}

/** What dropping into `name` says, as `entity`'s if there's one. */
const dropLabel = (name: string, entity?: Pick<ObjEntity, "name">): string =>
  entity ? t("folders.dropIntoAs", { name, entity: entity.name }) : t("folders.dropInto", { name });

/** Where files dropped on the items list (outside any folder) go: its top level, not the triage area; listing an
 *  entity's (`entity`), as its. */
export function topTarget(space: SpaceOut, entity?: ObjEntity): DropTarget {
  return {
    key: "folder:",
    label: dropLabel(t("folders.root"), entity),
    busy: t("folders.uploading"),
    level: 1,
    take: (files) => uploadInto(space, null, files, entity),
  };
}

/** Where files dropped on a folder's page go: into it; listing an entity's (`entity`), as its. */
export function folderTarget(space: SpaceOut, folder: FolderOut, entity?: ObjEntity): DropTarget {
  return {
    key: `folder:${folder.id}`,
    label: dropLabel(folder.name, entity),
    busy: t("folders.uploading"),
    level: 1,
    take: (files) => uploadInto(space, folder, files, entity),
  };
}
